import { LinearProgress } from '@mui/material';
import { AuthenticatedUserContext, ChosenThemeActionContext } from '@ngt/forms';
import * as Sentry from "@sentry/react";
import { FC, Suspense, useContext, useEffect, useMemo } from 'react';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider, useLocation } from 'react-router-dom';
import { UserDto } from './api/dtos';
import AdminAddSponsor from './components/admin/AdminAddSponsor';
import AdminBadgePrinting from './components/admin/AdminBadgePrinting';
import AdminCertificatesForm from './components/admin/AdminCertificatesForm';
import AdminHome from './components/admin/AdminHome';
import AdminRegistrationForm from './components/admin/AdminRegistrationForm';
import AdminRegistrationHome from './components/admin/AdminRegistrationHome';
import AdminReports from './components/admin/AdminReports';
import AdminSponsorHome from './components/admin/AdminSponsorHome';
import AdminAddSpeaker from './components/admin/speaker/AdminAddSpeaker';
import AdminSpeakerHome from './components/admin/speaker/AdminSpeakerHome';
import CardPage from './components/card/CardPage';
import ForbiddenPage from './components/common/ForbiddenPage';
import EarlyBirdRegistration from './components/earlyRegistration/EarlyBirdRegistration';
import RouteErrorBoundary from './components/errors/RouteErrorBoundary';
import RegistrationForm from './components/registration/RegistrationForm';
import RegistrationUpdateForm from './components/RegistrationUpdate/RegistrationUpdateForm';
import SpeakerForm from './components/speaker/SpeakerForm';
import { AppSettingsContext } from './contexts/AppSettingsContext';
import useSentryInitializer from './hooks/sentry/useSentryInitializer';

const LoginRedirect: FC = () => {
    const location = useLocation();

    useEffect(() => {
        window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(global.location.pathname + global.location.search)}`
    }, [location])

    return null;
}

const ForbiddenRedirect: FC = () => {
    return <Navigate to={`/forbidden`} replace />
}

const useRouter = (user: UserDto | undefined) => {
    const {data} = useContext(AppSettingsContext);

    return useMemo(() => {
        const defaultUrl = `/registration`;

        const publicRoutes: RouteObject[] = [
            {
                path: 'sponsor',
                element: (
                    <RegistrationForm sponsorMode={true} />
                ),
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'registration',
                element: (
                    <RegistrationForm />
                ),
                ErrorBoundary: RouteErrorBoundary
            },
            {
                //TODO: stop non speakers from accessing this route
                path: 'speaker/:guid',
                element: (
                    <SpeakerForm />
                ),
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'registration-update/:guid',
                element: (
                    <RegistrationUpdateForm />
                ),
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: '/card/:guid',
                element: (
                    <CardPage />
                ),
                ErrorBoundary: RouteErrorBoundary
            }
        ];

        const earlyBirdRoutes: RouteObject[] = [
            {
                path: 'early-bird/registration/:guid',
                element: (
                    <EarlyBirdRegistration />
                ),
                ErrorBoundary: RouteErrorBoundary
            }
        ];

        const adminOnlyRoutes: RouteObject[] = [
            {
                index: true,
                element: <AdminHome />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'registration',
                element: <AdminRegistrationForm />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'registration-desk',
                element: <AdminRegistrationHome />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'reports',
                element: <AdminReports />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'registration/:id',
                element: <AdminRegistrationForm />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'certificates',
                element: <AdminCertificatesForm />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'sponsor',
                element: <AdminSponsorHome />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'addSponsor',
                element: <AdminAddSponsor />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'addSponsor/:id',
                element: <AdminAddSponsor />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'custom-registrations',
                element: <AdminSpeakerHome />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'add-custom-registration',
                element: <AdminAddSpeaker />,
                ErrorBoundary: RouteErrorBoundary
            },
            {
                path: 'badge-printing',
                element: <AdminBadgePrinting />
            }
            
            // {
            //     path: 'speaker/:id',
            //     element: <AdminSpeakerForm />
            // }
        ];

        if (!user) {
            adminOnlyRoutes.forEach((x) => {
                x.element = <LoginRedirect />
            });
        }
        else if (!user.isAdmin) {
            adminOnlyRoutes.forEach((x) => {
                x.element = <ForbiddenRedirect />
            });
        }

        if (data?.earlyBirdRegistrationEndDate && new Date(data.earlyBirdRegistrationEndDate) < new Date() && !user?.isAdmin) {
            earlyBirdRoutes.forEach((x) => {
                x.element = <ForbiddenRedirect />
            });
        }
        
        const routes: RouteObject[] = [
            {
                path: "/",
                children: [
                    {
                        index: true,
                        element: <Navigate to={defaultUrl} replace />
                    },
                    ...publicRoutes,
                    {
                        path: 'admin',
                        children: [
                            ...adminOnlyRoutes
                        ]
                    },
                    ...earlyBirdRoutes,
                    {
                        path: 'forbidden',
                        element: <ForbiddenPage />
                    }
                ],
            },
        ];
    
        const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)(routes);

        return router;
    }, [user]);
}

const App: FC = () => {
    useSentryInitializer();
    const setChosenTheme = useContext(ChosenThemeActionContext);
    
    useEffect(() => {
        setChosenTheme('light');
    }, [])

    const { data: authUser } = useContext(AuthenticatedUserContext);
    const router = useRouter(authUser as UserDto);

    return (
        <Suspense fallback={<LinearProgress />}>
            <RouterProvider router={router} />
        </Suspense>
    );
}

export default App;